import { EncChannel, Encoding, MarkChannel } from '@mode-vegas/vegas';
import type * as ngRouter from '@uirouter/angularjs';
import { Observable } from 'rxjs';
import { combineLatestWith, distinctUntilChanged, filter, first, map, take, tap } from 'rxjs/operators';

import { ChartFacade, SelectedChartService } from '@mode/charts/contract';
import { AppStore } from '~/types/main.types';

import { ExprCall } from '@mode-switch/express';
import { fromChartSelectors } from '@mode/charts/domain';
import { BigValueHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { OrganizationResource } from '@mode/legacy/data-access-model-js';
import {
  ChartTypes,
  DataViewTypes,
  FeatureFlagsFacade,
  FlamingoTypes,
  OrgTypes,
  ReportTypes,
} from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { CompiledAttr } from '@mode/shared/util-switch';
import { DataViewFacade } from '@mode/switch-views/contract';
import { ChartDesignerActionType } from '~/actions/chart-designer/designer.actions';
import { getFirstFieldInChannel } from '~/lib/chart-encoding-helpers';
import { reportContextSelector } from '~/reducers/selectors';
import { CalcFieldsService } from '~/services/calc-fields-service';
import { ChartObservables } from '~/types/viz-designer.types';

class VizSettingsController {
  static $inject = [
    'RxStoreService',
    '$state',
    'ChartFacade',
    'FeatureFlagsFacade',
    'CalcFieldsService',
    'DataViewFacade',
  ];

  // bindings
  private chartService: SelectedChartService;
  private observables: ChartObservables;
  private readOnly: boolean;
  private objectContext: ChartTypes.ObjectType;

  // local state
  private selectedTab: 'fields' | 'format' = 'fields';
  private queryToken$: Observable<string>;
  private switchViewToken$: Observable<string>;
  private model$: Observable<FlamingoTypes.ModeModel>;
  private view$: Observable<FlamingoTypes.ModeView>;
  private renderModel$: Observable<RenderDataModel>;
  private format$: Observable<ChartTypes.ChartFormat>;
  private fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
  private encoding$: Observable<Encoding>;
  private chartType$: Observable<ChartTypes.ChartType>;
  private orgContext$: Observable<OrganizationResource>;
  private vizToken$: Observable<string>;
  private usedParameters: Observable<ReportTypes.ReportRunFormField[]>;
  private selectedFieldToFormat$: Observable<string> | null;

  private isBigValue$: Observable<boolean>;
  private isVegas$: Observable<boolean>;
  private planCode$: Observable<OrgTypes.PlanCode>;
  private isExploring: boolean;

  // Big Value Field Formats
  private trendTextFormat$: Observable<ChartTypes.TextFormat>;
  private valueTextFormat$: Observable<ChartTypes.TextFormat>;
  // End Big Value Field Formats

  constructor(
    private rxStore: AppStore,
    private $state: ngRouter.StateService,
    private chartFacade: ChartFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private calcFieldsService: CalcFieldsService,
    private dataViewFacade: DataViewFacade
  ) {}

  $onInit() {
    this.model$ = this.observables.model$;
    this.view$ = this.observables.view$;
    this.renderModel$ = this.observables.renderModel$;
    this.orgContext$ = this.rxStore.select(reportContextSelector);
    this.switchViewToken$ = this.observables.switchViewToken$;
    this.queryToken$ = this.observables.queryToken$;
    this.vizToken$ = this.observables.token$;
    this.format$ = this.observables.format$;
    this.encoding$ = this.observables.encoding$;
    this.chartType$ = this.observables.chartType$;
    this.fieldFormats$ = this.observables.fieldFormats$;

    this.isBigValue$ = this.chartType$.pipe(map((type) => type === 'bigValue'));
    this.isVegas$ = this.chartType$.pipe(
      map((type) => {
        const allowedChartTypes: ChartTypes.ChartType[] = ['vegas', 'vegasPivotTable'];
        return allowedChartTypes.indexOf(type) !== -1;
      })
    );
    this.planCode$ = this.orgContext$.pipe(map((org) => org.plan_code));

    // Big Value Observables
    const makeFieldFormatObs = (channel: MarkChannel) =>
      this.encoding$.pipe(
        map((encoding) => {
          const field = getFirstFieldInChannel(encoding, channel);
          return (field != null && field.formula) || null;
        }),
        distinctUntilChanged(),
        combineLatestWith(this.fieldFormats$),
        map(([formula, formats]) => BigValueHelpers.getPaneTextFormat(formats, formula))
      );

    this.valueTextFormat$ = makeFieldFormatObs('text');
    this.trendTextFormat$ = makeFieldFormatObs('detail');
    this.observables.token$.pipe(first()).subscribe((token) => {
      return (this.isExploring =
        token !== FLAMINGO_RESERVED_REFS.ChartExplorer &&
        (this.readOnly || this.objectContext === ChartTypes.ObjectType.Exploration));
    });

    this.selectedFieldToFormat$ = this.rxStore.select(fromChartSelectors.createAddSelectedFieldToFormatSelector());
    this.selectedFieldToFormat$
      .pipe(
        filter((field) => field !== null),
        tap(() => this.selectTab('format'))
      )
      .subscribe();
  }

  openCalcModal($event) {
    this.dataViewFacade
      .createSwitchViewBySwitchViewToken($event.switchViewToken)
      .pipe(take(1))
      .subscribe((switchView) => {
        const { switchViewToken, viewName, calc, viewMode, readOnly, duplicate } = $event;
        this.rxStore.dispatch({
          type: ChartDesignerActionType.OpenCalcEditor,
          switchViewToken,
          viewName,
          viewMode,
          calc,
          readOnly,
          duplicate,
          isImportedDataset: switchView?.sourceType === DataViewTypes.SwitchViewSourceType.SwitchView,
        });
      });
  }

  removeCalc($event) {
    const { switchViewToken, viewName, calc } = $event;
    this.calcFieldsService.deleteCalc({
      token: switchViewToken,
      queryToken: viewName,
      attr: calc,
    });
  }

  fieldDrag($field: CompiledAttr, $source: EncChannel | 'field-list', $valid: boolean) {
    this.chartService.startDrag({
      field: $field,
      source: $source,
      valid: $valid,
    });
  }

  applyFieldColor(attr: FlamingoTypes.ModeAttr, color: ChartTypes.FieldColor) {
    this.persistAttr(attr, { color });
  }

  applyFieldFormat(attr: FlamingoTypes.ModeAttr, format: ChartTypes.TextFormat) {
    this.persistAttr(attr, { format });
  }

  applyDefaultAgg(attr: FlamingoTypes.ModeAttr, defaultAgg: ExprCall.Name) {
    this.persistAttr(attr, { defaultsAggregate: defaultAgg });
  }

  persistAttr(attr: FlamingoTypes.ModeAttr, props: Partial<FlamingoTypes.ModeAttr>) {
    this.queryToken$
      .pipe(combineLatestWith(this.switchViewToken$), take(1))
      .subscribe(([queryToken, switchViewToken]) => {
        const oldAttr = { ...attr } as FlamingoTypes.ModeCalc;
        const newAttr = { ...attr, ...props } as FlamingoTypes.ModeCalc;

        if (attr.token) {
          this.calcFieldsService.updateCalc({
            token: switchViewToken,
            queryToken: queryToken,
            prevAttr: oldAttr,
            attr: newAttr,
          });
        } else {
          this.calcFieldsService.createCalc({
            token: switchViewToken,
            queryToken: queryToken,
            attr: newAttr,
          });
        }
      });
  }

  endFieldDrag() {
    this.chartFacade.endFieldDrag();
  }

  selectTab(tab) {
    this.selectedTab = tab;

    if (tab === 'fields' && this.selectedFieldToFormat$ !== null) {
      this.chartFacade.vizSettingsComponentRemoveSelectedFieldToFormat();
    }
  }
}

export const VizSettingsComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    chartService: '<',
    readOnly: '<',
    objectContext: '<',
    usedParameters: '<?',
  },
  template: `
    <encoding-builder
      model="ctrl.model$ | async:this"
      view-name="ctrl.queryToken$ | async:this"
      observables="ctrl.observables"
      chart-service="ctrl.chartService"
      format="ctrl.format$ | async:this"
      is-exploring="ctrl.isExploring"
    ></encoding-builder>
    <div class="tab-container chart-sidebar" ng-class="{'vegas-encoding-sidebar': (ctrl.isVegas$ | async:this)}">
      <ul class="nav nav-tabs nav-tabs-inline nav-tabs-bottom tabs">
        <li ng-class="{ active: ctrl.selectedTab === 'fields' }"><a href="#" ng-click="ctrl.selectTab('fields')">Fields</a></li>
        <li ng-class="{ active: ctrl.selectedTab === 'format' }"><a href="#" ng-click="ctrl.selectTab('format')">Format</a></li>
      </ul>
      <!-- TODO: Table & Chart Format -->
      <div ng-if="ctrl.selectedTab === 'fields'" class="fields-tab">
        <mode-field-list
          [view]="ctrl.view$ | async:this"
          [read-only]="ctrl.readOnly"
          (drag-start)="ctrl.fieldDrag($event.$field, 'field-list', $event.$valid)"
          (drag-end)="ctrl.endFieldDrag()"
          (trigger-calc-modal)="ctrl.openCalcModal($event)"
          (trigger-remove-calc)="ctrl.removeCalc($event)"
          (apply-field-color)="ctrl.applyFieldColor($event.attr, $event.color)"
          (apply-field-format)="ctrl.applyFieldFormat($event.attr, $event.format)"
          (apply-default-agg)="ctrl.applyDefaultAgg($event.attr, $event.defaultAgg)"
          [chart-object-type]="ctrl.objectContext"
          [switch-view-token]="ctrl.observables.switchViewToken$ | async:this"
          [query-run-token]="ctrl.observables.queryRunToken$ | async:this"
          [chart-type]="ctrl.observables.chartType$ | async:this"
        ></mode-field-list>
        <div ng-if="ctrl.objectContext === 'exploration'" class="ds-side-panel">
          <div ng-click="ctrl.isCollapsed = !ctrl.isCollapsed" class="ds-side-panel-header gray-state bordered-top-state collapsable-state">
            <h3 class="ds-side-panel-typography header-title-state">
              <i class="icon-caret-gray" ng-class="{'collapsed-state': ctrl.isCollapsed}"></i>
              Details
            </h3>
          </div>
          <div ng-if="!ctrl.isCollapsed">
            <div class="ds-side-panel-section">
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Data</p>
                <p class="ds-side-panel-typography centered-state link-state dataset-link-column">
                  <dataset-link query-token="ctrl.observables.queryToken$ | async:this"
                    query-name="ctrl.observables.switchViewName$ | async:this"
                    account-token="ctrl.$state.params.account"
                    report-token="ctrl.$state.params.reportId">
                  </dataset-link>
                </p>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Last ran</p>

                <p class="ds-side-panel-typography value-state">
                  <mode-time-ago
                    [date]="ctrl.observables.switchViewLastSuccessfulRunAt$ | async:this"
                  ></mode-time-ago>
                </p>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Refreshes</p>
                <p class="ds-side-panel-typography value-state">automatically</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <chart-format
        ng-if="ctrl.selectedTab === 'format' && !(ctrl.isBigValue$ | async:this) && !(ctrl.isVegas$ | async:this)"
        render-model="ctrl.renderModel$ | async:this"
        view-name="ctrl.queryToken$ | async:this"
        format="ctrl.format$ | async:this"
        encoding="ctrl.encoding$ | async:this"
        chart-type="ctrl.chartType$ | async:this"
        viz-token="ctrl.vizToken$ | async:this"
        owner="ctrl.orgContext$ | async:this"
        chart-service="ctrl.chartService"
        read-only="ctrl.readOnly">
      </chart-format>
      <mode-format-builder
        ng-if="ctrl.selectedTab === 'format' && (ctrl.isVegas$ | async:this)"
        [observables]="ctrl.observables"
        [view]="ctrl.view$ | async:this"
        [format]="ctrl.format$ | async:this"
        [field-formats]="ctrl.fieldFormats$ | async:this"
        [encoding]="ctrl.encoding$ | async:this"
        [chart-service]="ctrl.chartService"
        [chart-type]="ctrl.chartType$ | async:this"
        [selected-field-to-format]="ctrl.selectedFieldToFormat$ | async:this">
      </mode-format-builder>
      <big-value-format
        ng-if="ctrl.selectedTab === 'format' && (ctrl.isBigValue$ | async:this)"
        model="ctrl.model$ | async:this"
        encoding="ctrl.encoding$ | async:this"
        view-name="ctrl.queryToken$ | async:this"
        format="ctrl.format$ | async:this"
        trend-text-format="ctrl.trendTextFormat$ | async:this"
        value-text-format="ctrl.valueTextFormat$ | async:this"
        plan-code="ctrl.planCode$ | async:this"
        chart-service="ctrl.chartService">
      </big-value-format>
    </div>
  `,
  controller: VizSettingsController,
  controllerAs: 'ctrl',
};
